import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './styles.css';

const RangeSlider = ({
	min = 2014,
	max = 2023,
	from,
	to,
	value,
	type = 'double',
	step = 1,
	showGrid = true,
	gridNum = 9,
	onFinish,
	id,
	disabled = false,
}) => {
	// Ensure min, max, step are numbers
	const numMin = Number(min);
	const numMax = Number(max);
	const numStep = Number(step);
	const trackRef = useRef(null);
	const isUserChangeRef = useRef(false); // Track if change is from user interaction
	const [isDragging, setIsDragging] = useState(null); // 'from', 'to', 'single', or null
	const [hoverTooltip, setHoverTooltip] = useState(null); // { value, percent } or null
	const [fromValue, setFromValue] = useState(Number(from ?? numMin));
	const [toValue, setToValue] = useState(Number(to ?? numMax));
	const [singleValue, setSingleValue] = useState(Number(value ?? numMin));
	// Update internal state when props change (but only if not from user interaction)
	// This prevents overwriting user changes with stale prop values
	useEffect(() => {
		// Skip if this is a user-initiated change
		if (isUserChangeRef.current) {
			isUserChangeRef.current = false;
			return;
		}

		if (type === 'single') {
			if (value !== undefined) {
				const numValue = Number(value);
				// Only update if the prop value is different from current state
				if (numValue !== singleValue) {
					setSingleValue(numValue);
				}
			}
		} else {
			if (from !== undefined) {
				const numFrom = Number(from);
				if (numFrom !== fromValue) {
					setFromValue(numFrom);
				}
			}
			if (to !== undefined) {
				const numTo = Number(to);
				if (numTo !== toValue) {
					setToValue(numTo);
				}
			}
		}
	}, [type, from, to, value]);

	const getPercentage = useCallback(
		(val) => {
			return ((Number(val) - numMin) / (numMax - numMin)) * 100;
		},
		[numMin, numMax],
	);

	const getValueFromPosition = useCallback(
		(clientX) => {
			if (!trackRef.current) return numMin;
			const rect = trackRef.current.getBoundingClientRect();
			// Calculate position relative to track's left edge
			const relativeX = clientX - rect.left;
			// Ensure we're within track bounds
			const boundedX = Math.max(0, Math.min(rect.width, relativeX));
			const percentage = boundedX / rect.width;
			const rawValue = numMin + percentage * (numMax - numMin);
			const steppedValue = Math.round(rawValue / numStep) * numStep;
			const clampedValue = Math.max(numMin, Math.min(numMax, steppedValue));
			return clampedValue;
		},
		[numMin, numMax, numStep],
	);

	const handleTrackClick = useCallback(
		(e) => {
			if (disabled || isDragging) return;
			e.preventDefault();
			e.stopPropagation();

			const clientX = e.touches ? e.touches[0].clientX : e.clientX;
			const newValue = getValueFromPosition(clientX);

			if (type === 'single') {
				setSingleValue(newValue);
				isUserChangeRef.current = true; // Mark as user change
				if (onFinish) onFinish({ from: newValue });
			} else {
				// For double slider, determine which handle to move based on which is closer
				const fromDist = Math.abs(newValue - fromValue);
				const toDist = Math.abs(newValue - toValue);
				const midPoint = (fromValue + toValue) / 2;

				if (newValue < midPoint) {
					// Move 'from' handle
					const clampedValue = Math.min(newValue, toValue - numStep);
					setFromValue(clampedValue);
					isUserChangeRef.current = true; // Mark as user change
					if (onFinish) onFinish({ from: clampedValue, to: toValue });
				} else {
					// Move 'to' handle
					const clampedValue = Math.max(newValue, fromValue + numStep);
					setToValue(clampedValue);
					isUserChangeRef.current = true; // Mark as user change
					if (onFinish) onFinish({ from: fromValue, to: clampedValue });
				}
			}
		},
		[disabled, type, fromValue, toValue, singleValue, step, getValueFromPosition, onFinish, isDragging],
	);

	const handleStart = useCallback(
		(e, handleType) => {
			if (disabled) return;
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(handleType);

			const getClientX = (event) => {
				return event.touches ? event.touches[0].clientX : event.clientX;
			};

			let finalValue = null; // Track the final value during drag

			const handleMove = (moveEvent) => {
				moveEvent.preventDefault();
				const newValue = getValueFromPosition(getClientX(moveEvent));

				if (type === 'single') {
					setSingleValue(newValue);
					finalValue = { from: newValue };
					isUserChangeRef.current = true; // Mark as user change
				} else {
					if (handleType === 'from') {
						const clampedValue = Math.min(newValue, toValue - numStep);
						setFromValue(clampedValue);
						finalValue = { from: clampedValue, to: toValue };
						isUserChangeRef.current = true; // Mark as user change
					} else {
						const clampedValue = Math.max(newValue, fromValue + numStep);
						setToValue(clampedValue);
						finalValue = { from: fromValue, to: clampedValue };
						isUserChangeRef.current = true; // Mark as user change
					}
				}
			};

			const handleEnd = () => {
				setIsDragging(null);
				// Use the tracked final value instead of potentially stale state
				if (onFinish && finalValue) {
					onFinish(finalValue);
				}
				document.removeEventListener('mousemove', handleMove);
				document.removeEventListener('mouseup', handleEnd);
				document.removeEventListener('touchmove', handleMove);
				document.removeEventListener('touchend', handleEnd);
			};

			document.addEventListener('mousemove', handleMove);
			document.addEventListener('mouseup', handleEnd);
			document.addEventListener('touchmove', handleMove, { passive: false });
			document.addEventListener('touchend', handleEnd);
		},
		[disabled, type, fromValue, toValue, singleValue, numStep, getValueFromPosition, onFinish],
	);

	const handleKeyDown = useCallback(
		(e, handleType) => {
			if (disabled) return;

			let newValue;
			const currentValue = type === 'single' ? singleValue : handleType === 'from' ? fromValue : toValue;

			switch (e.key) {
				case 'ArrowLeft':
				case 'ArrowDown':
					e.preventDefault();
					newValue = Math.max(numMin, currentValue - numStep);
					break;
				case 'ArrowRight':
				case 'ArrowUp':
					e.preventDefault();
					newValue = Math.min(numMax, currentValue + numStep);
					break;
				case 'Home':
					e.preventDefault();
					newValue = numMin;
					break;
				case 'End':
					e.preventDefault();
					newValue = numMax;
					break;
				default:
					return;
			}

			if (type === 'single') {
				setSingleValue(newValue);
				isUserChangeRef.current = true; // Mark as user change
				if (onFinish) onFinish({ from: newValue });
			} else {
				if (handleType === 'from') {
					const clampedValue = Math.min(newValue, toValue - numStep);
					setFromValue(clampedValue);
					isUserChangeRef.current = true; // Mark as user change
					if (onFinish) onFinish({ from: clampedValue, to: toValue });
				} else {
					const clampedValue = Math.max(newValue, fromValue + numStep);
					setToValue(clampedValue);
					isUserChangeRef.current = true; // Mark as user change
					if (onFinish) onFinish({ from: fromValue, to: clampedValue });
				}
			}
		},
		[disabled, type, numMin, numMax, numStep, fromValue, toValue, singleValue, onFinish],
	);

	const fromPercent = type === 'single' ? getPercentage(singleValue) : getPercentage(fromValue);
	const toPercent = type === 'single' ? 0 : getPercentage(toValue);
	const leftPercent = type === 'single' ? 0 : fromPercent;
	const widthPercent = type === 'single' ? fromPercent : toPercent - fromPercent;

	// Ensure percentages are within bounds
	const clampedFromPercent = Math.max(0, Math.min(100, fromPercent));
	const clampedToPercent = Math.max(0, Math.min(100, toPercent));
	const clampedLeftPercent = Math.max(0, Math.min(100, leftPercent));
	const clampedWidthPercent = Math.max(0, Math.min(100, widthPercent));
	// Position tooltip above a point at the given percentage
	const getTooltipStyle = useCallback(
		(percent) => ({
			left: `calc(18px + ${percent}% - ${(percent * 36) / 100}px)`,
		}),
		[],
	);

	// Active tooltip: drag takes priority over hover
	const activeTooltip = isDragging
		? {
				value: type === 'single' ? singleValue : isDragging === 'from' ? fromValue : toValue,
				percent: type === 'single' ? clampedFromPercent : isDragging === 'from' ? clampedFromPercent : clampedToPercent,
			}
		: hoverTooltip;

	// Generate grid positions - one dot for each possible position based on step
	// Also determine if each dot is within the selected range
	const numPositions = Math.floor((numMax - numMin) / numStep) + 1;
	const gridPositions = showGrid
		? Array.from({ length: numPositions }, (_, i) => {
				const value = numMin + i * numStep;
				const position = getPercentage(value);
				// Check if this dot is within the selected range (on top of the bar)
				// Single: bar runs from min to handle, so value <= singleValue is on bar
				// Double: bar runs from from to to
				const isInRange = type === 'single' ? value <= singleValue : value >= fromValue && value <= toValue;
				return { position, isInRange, value };
			})
		: [];

	return (
		<div className={styles.rangeSlider} id={id} data-testid="range-slider">
			<div className={styles.trackBounds}>
				<div
					className={styles.track}
					ref={trackRef}
					data-testid="track"
					onMouseDown={(e) => {
						// Only handle clicks directly on the track background, not on children
						if (e.target === trackRef.current) {
							handleTrackClick(e);
						}
					}}
					onTouchStart={(e) => {
						if (e.target === trackRef.current) {
							handleTrackClick(e);
						}
					}}
				>
					{/* Grid dots */}
					{showGrid &&
						gridPositions.map((gridItem, index) => (
							<div
								key={`grid-${index}`}
								className={`${styles.gridDot} ${gridItem.isInRange ? styles.gridDotOnBar : styles.gridDotOnTrack}`}
								style={{ left: `${gridItem.position}%` }}
								aria-hidden="true"
								data-testid="grid-dot"
								data-in-range={gridItem.isInRange ? 'true' : 'false'}
								onMouseEnter={() => setHoverTooltip({ value: gridItem.value, percent: gridItem.position })}
								onMouseLeave={() => setHoverTooltip(null)}
							/>
						))}

					{/* Selected range bar */}
					<div
						className={styles.bar}
						style={{
							left: `${clampedLeftPercent}%`,
							width: `${clampedWidthPercent}%`,
						}}
						aria-hidden="true"
						data-testid="bar"
					/>

					{/* Handles */}
					{type === 'single' ? (
						<div
							className={`${styles.handle} ${isDragging === 'single' ? styles.dragging : ''}`}
							style={{ left: `${clampedFromPercent}%` }}
							onMouseDown={(e) => {
								e.stopPropagation();
								handleStart(e, 'single');
							}}
							onTouchStart={(e) => {
								e.stopPropagation();
								handleStart(e, 'single');
							}}
							onMouseEnter={() => setHoverTooltip({ value: singleValue, percent: clampedFromPercent })}
							onMouseLeave={() => setHoverTooltip(null)}
							onKeyDown={(e) => handleKeyDown(e, 'single')}
							tabIndex={disabled ? -1 : 0}
							role="slider"
							aria-valuemin={numMin}
							aria-valuemax={numMax}
							aria-valuenow={singleValue}
							aria-label="Select year"
							aria-disabled={disabled}
						/>
					) : (
						<>
							<div
								className={`${styles.handle} ${isDragging === 'from' ? styles.dragging : ''}`}
								style={{ left: `${clampedFromPercent}%` }}
								onMouseDown={(e) => {
									e.stopPropagation();
									handleStart(e, 'from');
								}}
								onTouchStart={(e) => {
									e.stopPropagation();
									handleStart(e, 'from');
								}}
								onMouseEnter={() => setHoverTooltip({ value: fromValue, percent: clampedFromPercent })}
								onMouseLeave={() => setHoverTooltip(null)}
								onKeyDown={(e) => handleKeyDown(e, 'from')}
								tabIndex={disabled ? -1 : 0}
								role="slider"
								aria-valuemin={numMin}
								aria-valuemax={toValue - numStep}
								aria-valuenow={fromValue}
								aria-label="Select start year"
								aria-disabled={disabled}
							/>
							<div
								className={`${styles.handle} ${isDragging === 'to' ? styles.dragging : ''}`}
								style={{ left: `${clampedToPercent}%` }}
								onMouseDown={(e) => {
									e.stopPropagation();
									handleStart(e, 'to');
								}}
								onTouchStart={(e) => {
									e.stopPropagation();
									handleStart(e, 'to');
								}}
								onMouseEnter={() => setHoverTooltip({ value: toValue, percent: clampedToPercent })}
								onMouseLeave={() => setHoverTooltip(null)}
								onKeyDown={(e) => handleKeyDown(e, 'to')}
								tabIndex={disabled ? -1 : 0}
								role="slider"
								aria-valuemin={fromValue + numStep}
								aria-valuemax={numMax}
								aria-valuenow={toValue}
								aria-label="Select end year"
								aria-disabled={disabled}
							/>
						</>
					)}
				</div>
			</div>

			{/* Min/max year labels */}
			<div className={styles.labels}>
				<span className={`${styles.label} ${styles.labelMin}`}>{numMin}</span>
				<span className={`${styles.label} ${styles.labelMax}`}>{numMax}</span>
			</div>

			{/* Custom tooltip — shown on hover or during drag */}
			{activeTooltip && (
				<div className={styles.dragTooltip} style={getTooltipStyle(activeTooltip.percent)}>
					{activeTooltip.value}
				</div>
			)}
		</div>
	);
};

export default RangeSlider;
